import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { ReviewsWidget } from "@/components/site/ReviewsWidget";
import { listTestimonialPosts } from "@/lib/blog.functions";
import { BRAND } from "@/lib/brand";
import { DirectAnswer, FAQSection, faqSchema } from "@/components/site/AnswerBlock";
import { TESTIMONIALS_ANSWER, TESTIMONIALS_FAQS } from "@/lib/aeo";
import { ResultsDisclaimer } from "@/components/site/ResultsDisclaimer";

const URL = "https://joinkcn.com/testimonials";

const storiesQuery = queryOptions({
  queryKey: ["testimonial-posts"],
  queryFn: () => listTestimonialPosts(),
});

export const Route = createFileRoute("/testimonials")({
  loader: ({ context }) => context.queryClient.ensureQueryData(storiesQuery),
  head: () => ({
    meta: [
      { title: "Real Estate Agent Testimonials - Kristan Cole Network" },
      {
        name: "description",
        content:
          "Real estate agent testimonials from the Kristan Cole Network, plus client reviews. Real stories from agents who doubled their business after joining KCN.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Testimonials - Kristan Cole Network" },
      {
        property: "og:description",
        content: "Agent and client stories from inside the Kristan Cole Network.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Kristan Cole Network testimonials",
          url: URL,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              item: {
                "@type": "Review",
                itemReviewed: { "@type": "RealEstateAgent", name: BRAND.name },
                reviewBody:
                  "Agents on the Kristan Cole Network describe growth in production, leverage, and time back with family after joining the team.",
                author: { "@type": "Organization", name: "KCN agents and clients" },
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
            { "@type": "ListItem", position: 2, name: "Testimonials", item: URL },
          ],
        }),
      },
      faqSchema(TESTIMONIALS_FAQS, URL),
    ],
  }),
  component: TestimonialsPage,
  errorComponent: ({ error }) => (
    <SiteLayout>
      <Section className="pt-36 md:pt-36 lg:pt-36">
        <p className="text-primary">Couldn't load stories: {error instanceof Error ? error.message : "Unknown error"}</p>
      </Section>
    </SiteLayout>
  ),
});

function TestimonialsPage() {
  const { data: stories } = useSuspenseQuery(storiesQuery);

  return (
    <SiteLayout>
      <section className="pt-36 pb-14 border-b border-border">
        <div className="container-edge">
          <p className="eyebrow mb-6">Testimonials</p>
          <h1 className="headline-xl max-w-5xl">
            The proof isn't the pitch.
            <br />
            <span className="serif-italic text-primary">It's the people.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-foreground/80 leading-relaxed">
            Agents who joined KCN and changed the shape of their business. Clients
            who felt the difference. In their words, not ours.
          </p>
        </div>
      </section>

      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-7">
            <SectionEyebrow>What people say</SectionEyebrow>
            <h2 className="headline-lg">
              From agents on the team and clients we've served.
            </h2>
          </div>
        </div>
        <ReviewsWidget />
      </Section>

      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-7">
            <SectionEyebrow>Success stories</SectionEyebrow>
            <h2 className="headline-lg">What changed after they joined.</h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-foreground/75 leading-relaxed">
              Longer-form accounts of agents who traded solo hustle for leverage.
              Same market. Same hours in the day. Different structure behind them.
            </p>
          </div>
        </div>

        {stories.length === 0 ? (
          <p className="text-foreground/65">More stories publishing soon.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
            {stories.map((s: any) => (
              <article key={s.slug} className="group h-full">
                <Link
                  to="/$"
                  params={{ _splat: s.slug }}
                  className="flex h-full flex-col border border-border bg-surface transition-colors hover:border-primary"
                >
                  {s.cover_image_url ? (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={s.cover_image_url}
                        alt={s.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="eyebrow mb-4">Success story</p>
                    <div className="font-display text-lg md:text-xl leading-snug mb-3 group-hover:text-primary transition-colors">
                      {s.title}
                    </div>
                    {s.excerpt ? (
                      <p className="text-foreground/75 text-sm leading-relaxed line-clamp-3">
                        {s.excerpt}
                      </p>
                    ) : null}
                    <span className="mt-auto pt-6 text-xs uppercase tracking-[0.18em] text-foreground/65 group-hover:text-primary transition-colors">
                      Read the story →
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
        <ResultsDisclaimer />
      </Section>

      <DirectAnswer question={TESTIMONIALS_ANSWER.question} answer={TESTIMONIALS_ANSWER.answer} />

      <FAQSection
        faqs={TESTIMONIALS_FAQS}
        eyebrow="Questions about these stories"
        heading="What agents ask about joining."
      />

      <CTABanner cta="strategy" />
    </SiteLayout>
  );
}
